import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, Check, Clock, RefreshCw, Zap, TrendingUp, TrendingDown,
  Power, ShieldOff, ChevronDown, ChevronRight, Download,
} from "lucide-react";
import { Skeleton } from "./ui/skeleton";
import { CollapseToggle } from "./CollapseToggle";
import { useCollapsed } from "../hooks/useCollapsed";
import { RejectionSummaryPanel } from "./RejectionSummaryPanel";
import { useAuth } from "../auth/AuthContext";
import { ExecutionJourney } from "./ExecutionJourney";

// 2026-08-21 — Setup A/C now capture buffer/spread/limit-price data at
// entry (see vwap_bounce_autopilot.py). Reuses the same ExecutionJourney
// panel Candle Breakout / OI Autopilot already use.
const hasExecutionJourney = (t) =>
  Boolean(
    t.buy_buffer_pct_actual != null || t.spread_pct_at_entry != null ||
    t.limit_order_price != null || t.live_buy_order_id
  );

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v, d = 2) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });

const toIST = (iso) => {
  if (!iso) return "—";
  try {
    let s = String(iso);
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s + "Z";
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date(s)).replace(",", "");
  } catch {
    return "—";
  }
};

const PatternBadge = ({ pattern }) => {
  if (!pattern) return <span className="text-muted-3">—</span>;
  const label = pattern === "two_strong_breakout" ? "2-STRONG" : "PULLBACK";
  return (
    <span
      className="inline-flex items-center gap-1 font-mono-data px-1.5 py-0.5 border text-[10px]"
      style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
    >
      <Zap size={11} />
      {label}
    </span>
  );
};

// Phase 48n — LONG/SHORT direction badge. Setup C is always SHORT; A & B are LONG.
const DirectionBadge = ({ direction, setup, testid }) => {
  const isShort = (direction || "").toUpperCase() === "SHORT";
  const color = isShort ? "var(--bear)" : "var(--bull)";
  const label = isShort ? "SHORT" : "LONG";
  const Icon = isShort ? TrendingDown : TrendingUp;
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 font-mono-data px-1.5 py-0.5 border text-[10px] uppercase tracking-wider"
      style={{ color, borderColor: color }}
      title={`${label}${setup ? ` · Setup ${setup}` : ""}`}
    >
      <Icon size={11} />
      {label}
      {setup && <span className="text-muted-3 normal-case">·{setup}</span>}
    </span>
  );
};

export const VWAPBounce = () => {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [trades, setTrades] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [liveBusy, setLiveBusy] = useState(false);
  const [collapsed, toggle] = useCollapsed("vwap-bounce", false);
  // Phase 49b — Per-table Show/Hide toggles (persisted in localStorage)
  const [picksCollapsed, togglePicks] = useCollapsed("vwap-bounce.picks", false);
  const [openCollapsed, toggleOpen] = useCollapsed("vwap-bounce.open", false);
  const [closedCollapsed, toggleClosed] = useCollapsed("vwap-bounce.closed", false);
  // 2026-08-21 — Execution Journey expand state (per trade id)
  const [journeyOpen, setJourneyOpen] = useState(() => new Set());
  const toggleJourney = (id) => setJourneyOpen((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [todayRes, tradesRes] = await Promise.all([
        axios.get(`${API}/vwap-bounce/today`),
        axios.get(`${API}/vwap-bounce/paper-trades`),
      ]);
      setData(todayRes.data);
      setTrades(tradesRes.data);
    } catch {
      toast.error("Failed to load VWAP Bounce");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const onScan = useCallback(async () => {
    setScanning(true);
    try {
      const r = await axios.post(`${API}/vwap-bounce/scan`);
      const qual = r?.data?.qualifying ?? 0;
      const entered = (r?.data?.auto_entered || []).length;
      toast.success(`VWAP Bounce: ${qual} qualifying · ${entered} auto-entered`);
      await load();
    } catch {
      toast.error("VWAP Bounce scan failed");
    } finally {
      setScanning(false);
    }
  }, [load]);

  // 2026-08-14 — live trading is now per-setup (A/B/C independent), so you
  // can e.g. enable Setup A while B and C stay paper-only.
  const liveBySetup = data?.live_trading || { A: false, B: false, C: false };
  const anyLive = Object.values(liveBySetup).some(Boolean);
  const allLive = Object.values(liveBySetup).every(Boolean);
  const liveEnabled = anyLive; // used by the summary badge below
  const onToggleLive = useCallback(async (setupId) => {
    const next = !liveBySetup[setupId];
    const verb = next ? "ENABLE" : "DISABLE";
    if (!window.confirm(
      `${verb} LIVE trading for Setup ${setupId} (VWAP Bounce)?\n\n` +
      (next
        ? "Auto-picks for THIS setup will place REAL Upstox BUY orders (1 lot ATM CE/PE, Delivery / NRML). Other setups are unaffected."
        : "New auto-entries for THIS setup will be paper-only. Existing OPEN live positions will still be exited at TP / SL / EOD.")
    )) return;
    setLiveBusy(true);
    try {
      await axios.post(`${API}/vwap-bounce/live-status`, { setup: setupId, enabled: next });
      toast.success(`VWAP Setup ${setupId} live trading ${next ? "ENABLED" : "DISABLED"}`);
      await load();
    } catch {
      toast.error(`Failed to flip Setup ${setupId} live-trading flag`);
    } finally {
      setLiveBusy(false);
    }
  }, [liveBySetup, load]);

  const onManualExit = useCallback(async (tradeId, symbol) => {
    if (!window.confirm(`Manually exit ${symbol}?\n\nThis will close at current LTP and trigger a SELL on Upstox for live trades.`)) return;
    try {
      await axios.post(`${API}/vwap-bounce/manual-exit`, { trade_id: tradeId });
      toast.success(`${symbol}: Manual exit done`);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Manual exit failed";
      toast.error(`${symbol}: ${detail}`);
    }
  }, [load]);

  // Aggregate the latest qualifying pick per symbol across today's runs
  // Also carry through the slot each pick came from (used to look up its
  // per-pick skip reason from that same run's entry_skip_log).
  const rows = useMemo(() => {
    if (!data?.runs) return [];
    const latest = new Map();
    for (const run of [...data.runs].reverse()) {
      for (const q of run.qualifying || []) {
        if (!q.symbol || latest.has(q.symbol)) continue;
        latest.set(q.symbol, { ...q, slot: run.slot, _run_id: run.id });
      }
    }
    return Array.from(latest.values()).slice(0, 5);
  }, [data]);

  const openTrades = data?.open_trades || [];
  const closedTrades = data?.closed_trades || [];
  const cap = data?.max_auto_picks ?? 2;
  const usedToday = openTrades.length + closedTrades.length;
  const lastRun = data?.runs?.[data.runs.length - 1];
  const warning = lastRun?.warning;
  const niftyAboveVwap = lastRun?.nifty_above_vwap;
  const rejectionSummary = lastRun?.rejection_summary || {};
  const universeSize = lastRun?.universe_size;

  // 2026-02-19 — Aggregate entry_skip_log across ALL of today's runs (not
  // just the last one) so the "Why no live trades?" panel captures skips
  // from earlier slots (e.g. NMDC skipped at 10:45 was invisible in the
  // 11:20 slot's log). Deduplicate by symbol+setup+direction, keeping the
  // most recent slot's reason.
  const { entrySkipLog, entrySkipSummary, skipReasonBySymbol } = useMemo(() => {
    const acc = new Map();
    const summary = {};
    const bySymbol = new Map();
    for (const run of data?.runs || []) {
      for (const s of run.entry_skip_log || []) {
        const key = `${s.symbol}::${s.setup_type || ""}::${s.direction || ""}`;
        acc.set(key, { ...s, slot: run.slot || run.id });
        bySymbol.set(s.symbol, { ...s, slot: run.slot || run.id });
      }
    }
    for (const [, v] of acc) {
      const reason = v.reason || "";
      // Bucket key is the token before the first ":" (matches how the
      // backend already summarises).
      const bucket = reason.split(":")[0].trim().replace(/ /g, "_") || "other";
      summary[bucket] = (summary[bucket] || 0) + 1;
    }
    return {
      entrySkipLog: Array.from(acc.values()),
      entrySkipSummary: summary,
      skipReasonBySymbol: bySymbol,
    };
  }, [data]);

  const liveTradesCount = (openTrades || []).filter((t) => t.live_trade).length
    + (closedTrades || []).filter((t) => t.live_trade).length;

  return (
    <section className="space-y-4" data-testid="vwap-bounce-section">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
            08 / Auto-Pilot · VWAP Bounce
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
            VWAP Bounce · 5-Min Auto-Picks
          </h2>
          <p className="text-xs text-muted-2 mt-1 max-w-3xl" data-testid="vwap-bounce-description">
              <strong style={{ color: allLive ? "var(--bull)" : anyLive ? "#FFB66E" : "var(--text-primary)" }}>
                {allLive ? "LIVE (ALL)" : anyLive ? "LIVE (PARTIAL)" : "PAPER"}
              </strong>{" · "}
              <strong>Three-setup VWAP scanner</strong> on the{" "}
              <strong>F&amp;O universe (~210 stocks with listed derivatives)</strong>, every 5 min. All three setups
              run independently in parallel — no mood gating.
              <strong> Mandatory RVOL floor</strong> (default 1.5×, <code>section8.scan_rvol_threshold</code>)
              applies to <strong>all three setups A/B/C</strong> as a hard reject, independent of each
              setup's own score/gates — this is one shared config key, not per-setup.
              <br />
              <strong>Setup A</strong> (VWAP Bounce LONG, strict · configurable window,
              default 10:00–14:00 IST) — 12-step Emergent Score. Fires at
              score ≥ <strong>setup_a_v3_score_min</strong> (default 85).
              Requires Nifty 50 &amp; stock above 20-EMA(5m), stock above VWAP, ADX(14) &gt; 25,
              tight VWAP proximity, ≤ 3 red pullback, bounce candle body &gt; 50% of range,
              bounce volume ≥ 1.5× 20-avg, RSI(9) 55-75, bullish 15-min HTF,
              R:R ≥ 2:1. <strong>Three hard gates sit outside the score entirely</strong> (score
              alone can't compensate for a fail): the shared RVOL floor above; a{" "}
              <strong>candle-close confirmation</strong> (close above open AND above prior
              close) — added after the OBEROIRLTY case where score/RVOL passed but price hadn't
              actually turned yet; and, added 2026-09-21, a{" "}
              <strong>bounce close-position gate</strong> (close must land in the upper 25% of
              the candle's own range) — moved out of the soft-scored list after an MCX entry
              scored 80/80 available and still fired on a 69.8%-of-range close.{" "}
              <strong>Cross-day lookback (2026-09-21)</strong>: ADX, RSI(9) and the 15-min HTF
              check are computed over [prior trading day's tail 5m candles + today's], not
              today-only — before this fix ADX and the 15-min HTF check (worth 20 of the 100
              score points combined) structurally couldn't be computed before ~11:45 / ~14:45
              IST (not enough intraday history yet), meaning Setup A was scored out of a lower
              real ceiling all morning even though the threshold stayed fixed — effectively
              unable to fire before ~11 AM most days. RSI(14) is also computed in parallel,
              cross-day, purely for comparison — it isn't a gate. Toggle:{" "}
              <strong>setup_a_v3_cross_day_lookback_enabled</strong> (default on). Near-miss
              band 70-84 shown for diagnostics.
              <br />
              <strong>Setup B</strong> (VWAP Bounce LONG, relaxed · default 09:30–14:00 IST)
              uses the Phase 53.3 v2 filter stack (§3.1–3.7) with 17 sequential gates, including
              the shared RVOL floor above.
              <br />
              <strong>Setup C V2</strong> (VWAP Rejection SHORT · default 10:00–14:00 IST · buys PE) —
              redesigned per <em>New_Spec_For_SetupC.md</em>. <strong>7 mandatory hard filters</strong>
              (tradable · Daily Supertrend bearish · Daily RSI &lt; 55 · price below VWAP ·
              VWAP-rejection within 1 % of high in last 3 bars · current bar bearish ·
              the shared RVOL floor above). Former hard gates (3 consecutive reds, strict monotone volume,
              wide lower wick, falling intraday RSI, VWAP/HTF slopes, ATR expansion) now
              feed a <strong>0-100 quality score</strong>. Qualifies when mandatory-all-pass
              AND score ≥ <strong>setup_c_v2_min_score</strong> (default 70).
              Confidence tiers: 90+ ★★★★★ · 80-89 ★★★★ · 70-79 ★★★. Near-miss band 60-69.
              Full <code>explain_score</code> object exposes points_awarded/deducted per rule.
              Open trades get a <strong>conditional time-stop</strong>: force-closed if held
              ≥ 45 min (<code>setup_c_time_stop_min</code>) while |P&amp;L| is still inside a
              ±3% dead-zone band (<code>setup_c_time_stop_pnl_band_pct</code>) — trades already
              trending toward target/SL are left alone. Setup A is deliberately excluded (its
              long-held trades perform fine regardless of duration).
              <br />
              Near-miss surfaces any pick that clears ≥ 70 % of gates but fails a later stage
              (Setup B: gate-stage scoring · Setup C: quality-score band 60-69),
              so you see the exact failing gate.
              <br />
              <strong>Low-conviction 1 PM window</strong> (default 12:45–13:45 IST,
              <code> section8.rvol_window_*</code>): 14-day journal data showed this window
              losing heavily across both directions and both Sniper + VWAP Bounce, so Setup A
              and C additionally require RVOL ≥ 3× to enter during it (Setup B not yet in scope).
              Every candidate seen in the window — pass or reject — is logged to
              <code> rvol_window_gate_log</code> for later evaluation.
              <br />
              Entry windows are admin-configurable via <code>section8.setup_[a|b|c]_start_hh/mm</code>
              and <code>_end_hh/mm</code>.
              <br />
              <strong>Max 2 LONG + 2 SHORT</strong> auto-picks per day · Used {usedToday}.
              <strong> Option chase guard</strong>: skips ATM strikes already up ≥ 50%
              from open (all setups). <strong>Setup A</strong> additionally applies its own
              tighter pair: skip if already up ≥{" "}
              <strong>setup_a_max_option_chase_pct</strong>% (default 8%), or — added
              2026-09-21 — already down ≥ <strong>setup_a_max_option_crash_pct</strong>%
              (default 15%) from open (a crashing premium is rarely "the start of a bounce").
              EOD force-close 15:15 IST.
            </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center border border-grid divide-x divide-grid">
            {["A", "B", "C"].map((setupId) => {
              const on = Boolean(liveBySetup[setupId]);
              return (
                <button
                  key={setupId}
                  onClick={() => onToggleLive(setupId)}
                  disabled={liveBusy}
                  data-testid={`vwap-bounce-live-toggle-${setupId}`}
                  // 2026-09-12 (chat, re: screenshot — "When it's Live, can
                  // you make it bit dark or highlight it? B in this case,
                  // isn't looking good") — previously ON vs OFF was only a
                  // text-color difference (var(--bull) vs var(--text-muted))
                  // on the same transparent background, which read as barely
                  // different from its OFF siblings in the segmented control.
                  // Now the live segment also gets a filled bull-tinted
                  // background (.bull-bg — same var(--bull-muted) wash used
                  // elsewhere in the app) so it's unmistakably the active one.
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] uppercase tracking-[0.15em] font-mono-data transition-colors ${on ? "bull-bg" : "hover:bg-white/[0.04]"}`}
                  style={{ color: on ? "var(--bull)" : "var(--text-muted)" }}
                  title={
                    on
                      ? `Setup ${setupId} live trading ON — click to pause`
                      : `Setup ${setupId} paper only — click to enable Live`
                  }
                >
                  {on ? <Power size={11} /> : <ShieldOff size={11} />}
                  {setupId}
                </button>
              );
            })}
          </div>
          <button
            onClick={onScan}
            disabled={scanning}
            data-testid="vwap-bounce-scan-now"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-grid text-[10px] uppercase tracking-[0.2em] font-mono-data hover:bg-white/[0.04]"
          >
            <RefreshCw size={12} className={scanning ? "animate-spin" : ""} />
            {scanning ? "Scanning…" : "Scan Now"}
          </button>
          <CollapseToggle collapsed={collapsed} onToggle={toggle} testid="vwap-bounce-collapse-toggle" />
        </div>
      </div>

      {!collapsed && (
        <>
          {warning && (
            <div className="border border-grid p-3 text-xs text-muted-2 font-mono-data" data-testid="vwap-bounce-warning">
              <Clock size={12} className="inline mr-2" /> {warning}
            </div>
          )}

          {loading && !data ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <>
              {/* Why no picks? — visible whenever today's last run yielded 0 qualifying */}
              {rows.length === 0 && (
                <RejectionSummaryPanel
                  testid="vwap-bounce-rejection-summary"
                  title="Why no picks?"
                  slot={lastRun?.slot}
                  universeSize={universeSize}
                  rejectionSummary={rejectionSummary}
                  extraBadges={
                    niftyAboveVwap !== undefined && (
                      <span
                        style={{
                          color: niftyAboveVwap ? "var(--bull)" : "var(--bear)",
                        }}
                      >
                        Nifty {niftyAboveVwap ? "ABOVE" : "BELOW"} intraday VWAP{" "}
                        <span className="text-muted-3">(advisory)</span>
                      </span>
                    )
                  }
                  footerNote={
                    <>
                      Hover any badge to see what that gate actually checks. The Nifty
                      intraday VWAP flag is informational only — it no longer blocks picks.
                    </>
                  }
                />
              )}

              {/* Near-Miss Panel — Setup A (steps/score) + Setup B/C (gate-stage progression) */}
              {(lastRun?.near_misses?.length || 0) > 0 && (
                <div className="border border-grid p-4 space-y-3"
                      data-testid="vwap-bounce-near-miss-panel">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data flex items-center gap-2">
                      <Activity size={12} />
                      Near-Misses (A/B/C)
                      <span className="text-muted-3 normal-case">
                        · Top {lastRun.near_misses.length} candidates just below the qualification threshold
                      </span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full font-mono-data text-[11px]" data-testid="vwap-bounce-near-miss-table">
                      <thead>
                        <tr className="text-muted-3 uppercase tracking-widest text-[9px]">
                          <th className="text-left px-2 py-1 border-b border-grid">Setup</th>
                          <th className="text-left px-2 py-1 border-b border-grid">Symbol</th>
                          <th className="text-right px-2 py-1 border-b border-grid">Close</th>
                          <th className="text-right px-2 py-1 border-b border-grid">Score</th>
                          <th className="text-left px-2 py-1 border-b border-grid">Failing Gate</th>
                          <th className="text-left px-2 py-1 border-b border-grid">Detail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lastRun.near_misses.map((nm, i) => {
                          const isSetupA = nm.setup_type === "A";
                          // Setup A — use `steps` dict; B/C use `passed_gates`/`failing_gate`
                          const stepEntries = Object.entries(nm.steps || {});
                          const bestStep = stepEntries
                            .filter(([, v]) => (v?.awarded ?? 0) > 0)
                            .sort(([, a], [, b]) => (b.awarded || 0) - (a.awarded || 0))[0];
                          const failing = isSetupA
                            ? ((nm.failing_steps || []).join(", ") || "score below threshold")
                            : (nm.failing_gate || "—");
                          const detail = isSetupA
                            ? (bestStep ? `Best: ${bestStep[0]} +${bestStep[1].awarded}` : "—")
                            : `${nm.gate_stage || "?"} / ${nm.total_gates || "?"} gates passed`;
                          return (
                            <tr key={i} className="hover:bg-white/[0.02]"
                                data-testid={`near-miss-row-${nm.symbol}-${nm.setup_type}`}>
                              <td className="px-2 py-1 uppercase text-muted-2 text-[10px]">
                                {nm.setup_type || "A"}
                              </td>
                              <td className="px-2 py-1" style={{ color: "var(--bull)" }}>{nm.symbol}</td>
                              <td className="px-2 py-1 text-right">{nm.close ?? "—"}</td>
                              <td className="px-2 py-1 text-right">
                                <span style={{ color: "var(--warn)" }}>{nm.score}</span>
                                <span className="text-muted-3">/{nm.score_min || 100}</span>
                              </td>
                              <td className="px-2 py-1 text-muted-2 text-[10px]">{failing}</td>
                              <td className="px-2 py-1 text-[10px]" style={{ color: "var(--bull)" }}>
                                {detail}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="text-[10px] text-muted-3 font-mono-data">
                    ℹ Diagnostic only — these are <em>not</em> entered as paper trades. Setup A near-miss floor:{" "}
                    <code>setup_a_v3_near_miss_min</code> (default 70). Setup B/C near-miss floor:{" "}
                    <code>near_miss_min</code> (default 70) in Admin → Section 8.
                  </div>
                </div>
              )}

              {/* Phase 48b — Why no LIVE trades? Visible whenever we had qualifying
                  picks but at least one was skipped during the live-entry stage.
                  Surfaces cap, duplicate, spread, chase-guard, broker errors. */}
              {(entrySkipLog?.length > 0 || (rows.length > 0 && liveTradesCount === 0)) && (
                <div
                  className="border border-grid p-4 space-y-3"
                  data-testid="vwap-bounce-entry-skip-panel"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data flex items-center gap-2">
                      <Clock size={12} />
                      Why no live trades?
                      <span className="text-muted-3 normal-case">
                        · {entrySkipLog.length} pick{entrySkipLog.length === 1 ? "" : "s"} skipped at entry
                      </span>
                    </div>
                    <div className="text-[10px] font-mono-data" style={{ color: liveTradesCount > 0 ? "var(--bull)" : "var(--bear)" }}>
                      {liveTradesCount} LIVE today
                    </div>
                  </div>
                  {Object.keys(entrySkipSummary).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(entrySkipSummary)
                        .sort((a, b) => b[1] - a[1])
                        .map(([bucket, count]) => (
                          <span
                            key={bucket}
                            className="inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-mono-data"
                            style={{ borderColor: "var(--grid)", color: "var(--text-primary)" }}
                            data-testid={`vwap-skip-bucket-${bucket}`}
                          >
                            <strong>{count}×</strong>
                            <span className="text-muted-2">{bucket.replace(/_/g, " ")}</span>
                          </span>
                        ))}
                    </div>
                  )}
                  <details className="text-[10px] text-muted-2">
                    <summary className="cursor-pointer text-muted-3 uppercase tracking-[0.2em] font-mono-data">
                      Show per-pick detail ({entrySkipLog.length})
                    </summary>
                    <table className="mt-2 w-full text-[10px] font-mono-data">
                      <thead className="text-muted-3 uppercase tracking-[0.15em]">
                        <tr>
                          <th className="text-left py-1 pr-3">Symbol</th>
                          <th className="text-left py-1 pr-3">Setup · Dir</th>
                          <th className="text-left py-1">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entrySkipLog.map((row, idx) => {
                          const reason = row.reason || "";
                          // Broker connectivity issues = RED; filter skips = AMBER; caps/dedup = muted
                          const isBrokerError = reason.startsWith("live_order_fallback_to_paper");
                          const isFilterSkip = reason.startsWith("live_skip_filter");
                          const color = isBrokerError
                            ? "var(--bear)"
                            : isFilterSkip
                            ? "var(--warn, #d97706)"
                            : "var(--muted-2, #888)";
                          return (
                            <tr
                              key={`${row.symbol}-${idx}`}
                              className="border-t border-grid"
                              data-testid={`vwap-skip-row-${idx}`}
                            >
                              <td className="py-1 pr-3 text-foreground">{row.symbol}</td>
                              <td className="py-1 pr-3">
                                {row.setup_type || "—"} · {row.direction || "LONG"}
                              </td>
                              <td className="py-1" style={{ color }}>{reason}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </details>
                  <div className="text-[10px] text-muted-3 leading-relaxed">
                    <strong>Tip:</strong> Reasons fall into 3 buckets — <em>filter skips</em> (paper trade recorded; algo blocked the live order)
                    like <em>live_skip_filter</em> (red-candle / VWAP-bounce / volume / chase), <em>option_already_chased</em>, <em>wide_spread</em>;
                    <em> cap / dedup</em> (<em>long_cap_reached</em>, <em>short_cap_reached</em>, <em>duplicate_live_buy_already_today</em>);
                    and <em>broker errors</em> (<em>live_order_fallback_to_paper</em> — Upstox actually rejected the order; check token / margin).
                  </div>
                </div>
              )}

              {/* Qualifying picks table */}
              <div className="border border-grid">
                <div className="px-4 py-2 border-b border-grid text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Activity size={12} /> Today&apos;s Qualifying Picks
                    {rows.length > 0 && <span className="text-primary">· {rows.length}</span>}
                  </div>
                  <CollapseToggle
                    collapsed={picksCollapsed}
                    onToggle={togglePicks}
                    testid="vwap-bounce-picks-toggle"
                  />
                </div>
                {!picksCollapsed && (
                  rows.length === 0 ? (
                    <div className="px-4 py-6 text-center text-xs text-muted-3 font-mono-data" data-testid="vwap-bounce-no-picks">
                      No qualifying picks yet for today
                    </div>
                  ) : (
                    <div className="overflow-x-auto" data-testid="vwap-bounce-picks-body">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-grid text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
                            <th className="px-4 py-2 text-left">Slot</th>
                            <th className="px-4 py-2 text-left">Symbol</th>
                            <th className="px-4 py-2 text-left">Dir</th>
                            <th className="px-4 py-2 text-left">Pattern</th>
                            <th className="px-4 py-2 text-right">LTP</th>
                            <th className="px-4 py-2 text-right">% Chg</th>
                            <th className="px-4 py-2 text-right">VWAP</th>
                            <th className="px-4 py-2 text-right">RSI</th>
                            <th className="px-4 py-2 text-right">RVOL</th>
                            <th className="px-4 py-2 text-right">ATR-SL</th>
                            <th className="px-4 py-2 text-right">ATM</th>
                            <th className="px-4 py-2 text-right" title="Option premium's own move since today's 9:15 AM open — how 'chased' the entry already is. Entries are blocked at/above the chase threshold.">Opt % Chg</th>
                            <th className="px-4 py-2 text-right">Target / SL</th>
                            <th className="px-4 py-2 text-left">Entry Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r, i) => {
                            // Correlate this pick with today's open/closed trades + entry_skip_log
                            const skip = skipReasonBySymbol.get(r.symbol);
                            const traded = [...openTrades, ...closedTrades].find(
                              (t) => t.symbol === r.symbol && t.setup_type === r.setup_type,
                            );
                            let statusLabel = "PENDING";
                            let statusColor = "var(--text-muted)";
                            let statusDetail = "";
                            if (traded?.live_trade) {
                              statusLabel = "LIVE";
                              statusColor = "var(--bull)";
                              statusDetail = "Real Upstox order placed";
                            } else if (traded?.auto_entered) {
                              statusLabel = "PAPER";
                              statusColor = "#FFB66E";
                              statusDetail = "Paper trade recorded";
                            } else if (skip) {
                              statusLabel = "SKIPPED";
                              statusColor = "var(--bear)";
                              statusDetail = skip.reason || "";
                            }
                            return (
                            <tr key={r.symbol + i} className="border-b border-grid hover:bg-white/[0.02]" data-testid={`vwap-bounce-row-${i}`}>
                              <td className="px-4 py-2 text-muted-2 font-mono-data">{r.slot}</td>
                              <td className="px-4 py-2 font-semibold">{r.symbol}</td>
                              <td className="px-4 py-2">
                                <DirectionBadge direction={r.direction} setup={r.setup_type} testid={`vwap-bounce-row-dir-${i}`} />
                              </td>
                              <td className="px-4 py-2"><PatternBadge pattern={r.pattern} /></td>
                              <td className="px-4 py-2 text-right font-mono-data">₹{fmt(r.close)}</td>
                              <td
                                className="px-4 py-2 text-right font-mono-data"
                                data-testid={`vwap-bounce-row-change-${i}`}
                                style={{
                                  color: r.change_pct == null
                                    ? "var(--text-muted)"
                                    : r.change_pct >= 0 ? "var(--bull)" : "var(--bear)",
                                }}
                              >
                                {r.change_pct == null
                                  ? <span className="text-muted-3">—</span>
                                  : `${r.change_pct >= 0 ? "+" : ""}${fmt(r.change_pct, 2)}%`}
                              </td>
                              <td className="px-4 py-2 text-right font-mono-data">₹{fmt(r.vwap)}</td>
                              <td className="px-4 py-2 text-right font-mono-data" style={{ color: "var(--bull)" }}>{fmt(r.rsi, 1)}</td>
                              <td
                                className="px-4 py-2 text-right font-mono-data"
                                data-testid={`vwap-bounce-row-rvol-${i}`}
                                style={{ color: r.rvol_exhaustion ? "var(--warn, #d97706)" : (r.rvol ?? 0) >= 1.5 ? "var(--bull)" : "var(--text-muted)" }}
                                title={r.rvol_exhaustion ? "High RVOL — possible climax/exhaustion volume, review before trusting continuation" : undefined}
                              >
                                {r.rvol != null ? `${fmt(r.rvol, 2)}×${r.rvol_exhaustion ? " ⚠" : ""}` : <span className="text-muted-3">—</span>}
                              </td>
                              <td className="px-4 py-2 text-right font-mono-data">₹{fmt(r.sl)}</td>
                              <td className="px-4 py-2 text-right font-mono-data">
                                {r.atm_strike
                                  ? `${r.atm_strike} ${(r.direction || "LONG").toUpperCase() === "SHORT" ? "PE" : "CE"} @ ₹${fmt(r.atm_ce_ltp)}`
                                  : <span className="text-muted-3">—</span>}
                              </td>
                              <td
                                className="px-4 py-2 text-right font-mono-data"
                                data-testid={`vwap-bounce-row-optmove-${i}`}
                                title={r.opt_day_open != null ? `Day open ₹${fmt(r.opt_day_open)} · chase limit ${fmt(r.opt_chase_threshold_pct ?? 50, 0)}%` : ""}
                                style={{
                                  color: r.opt_move_pct == null ? "var(--text-muted)"
                                    : r.opt_move_pct >= (r.opt_chase_threshold_pct ?? 50) ? "var(--bear)"
                                    : r.opt_move_pct >= (r.opt_chase_threshold_pct ?? 50) * 0.7 ? "#FFB66E"
                                    : "var(--text-muted)",
                                }}
                              >
                                {r.opt_move_pct == null
                                  ? <span className="text-muted-3">—</span>
                                  : `${r.opt_move_pct >= 0 ? "+" : ""}${fmt(r.opt_move_pct, 0)}%`}
                              </td>
                              <td className="px-4 py-2 text-right font-mono-data text-[10px]">
                                <span style={{ color: "var(--bull)" }}>+{fmt(r.target_pct ?? 9, 1)}%</span>
                                <span className="text-muted-3"> / </span>
                                <span style={{ color: "var(--bear)" }}>{fmt(r.stop_loss_pct ?? -15, 1)}%</span>
                              </td>
                              <td className="px-4 py-2 text-left" data-testid={`vwap-bounce-row-status-${i}`}>
                                <div className="flex flex-col gap-0.5">
                                  <span
                                    className="inline-block px-1.5 py-0.5 border font-mono-data text-[10px] uppercase tracking-wider w-fit"
                                    style={{ color: statusColor, borderColor: statusColor }}
                                    title={statusDetail}
                                    data-testid={`vwap-bounce-row-status-badge-${i}`}
                                  >
                                    {statusLabel}
                                  </span>
                                  {statusDetail && statusLabel === "SKIPPED" && (
                                    <span
                                      className="text-[9px] font-mono-data text-muted-2 max-w-[220px] leading-tight"
                                      data-testid={`vwap-bounce-row-status-reason-${i}`}
                                    >
                                      {statusDetail}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )
                )}
              </div>

              {/* Open trades */}
              {openTrades.length > 0 && (
                <div className="border border-grid">
                  <div className="px-4 py-2 border-b border-grid text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <TrendingUp size={12} /> Open Trades · {openTrades.length}
                    </div>
                    <CollapseToggle
                      collapsed={openCollapsed}
                      onToggle={toggleOpen}
                      testid="vwap-bounce-open-toggle"
                    />
                  </div>
                  {!openCollapsed && (
                  <div className="overflow-x-auto" data-testid="vwap-bounce-open-body">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-grid text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
                          <th className="px-4 py-2 text-left">Entered</th>
                          <th className="px-4 py-2 text-left">Symbol</th>
                          <th className="px-4 py-2 text-left">Dir</th>
                          <th className="px-4 py-2 text-right">Strike</th>
                          <th className="px-4 py-2 text-right" title="Relative Volume at entry (latest 5m / avg-10)">RVOL</th>
                          <th className="px-4 py-2 text-right" title="Underlying's intraday RSI on the 5-min chart at entry (Setup A/C). Setup A uses RSI(9), cross-day lookback (2026-09-21) — real value from market open, not blank until ~10 AM. Setup C highlights <35 as oversold; Setup A already requires 55-75 to enter so it's shown plain, no highlight.">Intraday RSI</th>
                          <th className="px-4 py-2 text-right" title="Setup A only. Parallel, observational RSI(14), cross-day lookback — not a gate, kept for RSI(9) vs RSI(14) comparison.">RSI(14)</th>
                          <th className="px-4 py-2 text-right" title="Setup A only. ADX(14) at entry, cross-day lookback (2026-09-21) so it's a real value from market open, not blank until ~11:45 AM. Must be ≥ 25 to score the adx step's 10 points.">ADX</th>
                          <th className="px-4 py-2 text-center" title="Setup A only. 15-min-timeframe trend check at entry (price above its own VWAP and 20-EMA, last 15m candle bullish) — cross-day lookback (2026-09-21) so it's real from market open, not blank until ~2:45 PM. Worth 10 of the 100 score points.">15m HTF</th>
                          <th className="px-4 py-2 text-right" title="Option premium's own move since today's 9:15 AM open, at entry time">Opt % Chg</th>
                          <th className="px-4 py-2 text-right">Buy LTP</th>
                          <th className="px-4 py-2 text-right">Current</th>
                          <th className="px-4 py-2 text-right">P&amp;L</th>
                          <th className="px-4 py-2 text-left">Mode</th>
                          <th className="px-4 py-2 text-right">Target / SL</th>
                          <th className="px-4 py-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {openTrades.map((t, i) => (
                          <React.Fragment key={t.id}>
                          <tr className="border-b border-grid" data-testid={`vwap-bounce-open-${i}`}>
                            <td className="px-4 py-2 text-muted-2 font-mono-data">
                              <div className="inline-flex items-center gap-1">
                                {hasExecutionJourney(t) && (
                                  <button
                                    onClick={() => toggleJourney(t.id)}
                                    aria-expanded={journeyOpen.has(t.id)}
                                    aria-label={journeyOpen.has(t.id) ? "Hide Execution Journey" : "Show Execution Journey"}
                                    data-testid={`vwap-bounce-open-journey-toggle-${i}`}
                                    className="text-muted-2 hover:text-primary"
                                    title="Toggle Execution Journey"
                                  >
                                    {journeyOpen.has(t.id) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                  </button>
                                )}
                                <span>{toIST(t.entered_at)}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2 font-semibold">{t.symbol}</td>
                            <td className="px-4 py-2">
                              <DirectionBadge direction={t.direction} setup={t.setup_type} testid={`vwap-bounce-open-dir-${i}`} />
                            </td>
                            <td className="px-4 py-2 text-right font-mono-data">
                              {t.atm_strike} {((t.direction || "LONG").toUpperCase() === "SHORT") ? "PE" : "CE"}
                            </td>
                            <td
                              className="px-4 py-2 text-right font-mono-data"
                              data-testid={`vwap-bounce-open-rvol-${i}`}
                              style={{ color: t.rvol_exhaustion ? "var(--warn, #d97706)" : (t.rvol ?? 0) >= 1.5 ? "var(--bull)" : "var(--text-muted)" }}
                              title={t.rvol_exhaustion ? "High RVOL at entry — possible climax/exhaustion volume" : undefined}
                            >
                              {t.rvol != null ? `${fmt(t.rvol, 2)}×${t.rvol_exhaustion ? " ⚠" : ""}` : <span className="text-muted-3">—</span>}
                            </td>
                            <td
                              className="px-4 py-2 text-right font-mono-data"
                              data-testid={`vwap-bounce-open-rsi-intraday-${i}`}
                              // 2026-09-21 — oversold-below-35 highlight is Setup C's
                              // SHORT thesis only; Setup A already gates entry at
                              // RSI 55-75 so the same <35 "oversold" framing doesn't
                              // apply — shown plain there (chat: "shall we start
                              // populating Intraday RSI?").
                              style={{ color: t.setup_type === "C" && t.rsi_intraday != null && t.rsi_intraday < 35 ? "var(--bear)" : "var(--text-muted)" }}
                              title={t.setup_type === "C" && t.rsi_intraday != null && t.rsi_intraday < 35 ? "Below 35 — underlying already deeply oversold on the 5-min chart" : ""}
                            >
                              {t.rsi_intraday != null ? fmt(t.rsi_intraday, 1) : <span className="text-muted-3">—</span>}
                            </td>
                            <td
                              className="px-4 py-2 text-right font-mono-data text-muted-2"
                              data-testid={`vwap-bounce-open-rsi14-${i}`}
                            >
                              {t.rsi_14_intraday != null ? fmt(t.rsi_14_intraday, 1) : <span className="text-muted-3">—</span>}
                            </td>
                            <td
                              className="px-4 py-2 text-right font-mono-data"
                              data-testid={`vwap-bounce-open-adx-${i}`}
                              style={{ color: t.adx_at_entry != null && t.adx_at_entry >= 25 ? "var(--bull)" : "var(--text-muted)" }}
                            >
                              {t.adx_at_entry != null ? fmt(t.adx_at_entry, 1) : <span className="text-muted-3">—</span>}
                            </td>
                            <td
                              className="px-4 py-2 text-center font-mono-data"
                              data-testid={`vwap-bounce-open-htf15m-${i}`}
                              style={{ color: t.htf_15m_ok === true ? "var(--bull)" : t.htf_15m_ok === false ? "var(--bear)" : "var(--text-muted)" }}
                            >
                              {t.htf_15m_ok === true ? "Yes" : t.htf_15m_ok === false ? "No" : <span className="text-muted-3">—</span>}
                            </td>
                            <td
                              className="px-4 py-2 text-right font-mono-data"
                              data-testid={`vwap-bounce-open-optmove-${i}`}
                              title={t.opt_day_open != null ? `Day open ₹${fmt(t.opt_day_open)} · chase limit ${fmt(t.opt_chase_threshold_pct ?? 50, 0)}%` : ""}
                              style={{
                                color: t.opt_move_pct == null ? "var(--text-muted)"
                                  : t.opt_move_pct >= (t.opt_chase_threshold_pct ?? 50) * 0.7 ? "#FFB66E"
                                  : "var(--text-muted)",
                              }}
                            >
                              {t.opt_move_pct == null
                                ? <span className="text-muted-3">—</span>
                                : `${t.opt_move_pct >= 0 ? "+" : ""}${fmt(t.opt_move_pct, 0)}%`}
                            </td>
                            <td className="px-4 py-2 text-right font-mono-data" data-testid={`vwap-bounce-open-entry-${i}`}>
                              {t.live_trade && t.live_avg_fill_price ? (
                                <span title={`Broker fill ₹${fmt(t.live_avg_fill_price)} (spot at signal was ₹${fmt(t.atm_ce_ltp)})`}>
                                  ₹{fmt(t.live_avg_fill_price)}
                                  <span className="ml-1 text-[9px] text-muted-3">FILL</span>
                                </span>
                              ) : (
                                <>₹{fmt(t.atm_ce_ltp)}</>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right font-mono-data" data-testid={`vwap-bounce-open-current-${i}`}>
                              {t.live_sell_price ? (
                                <span title={`Broker sell fill · basis: ${t.pnl_basis || 'reference'}`}>
                                  ₹{fmt(t.live_sell_price)}
                                  <span className="ml-1 text-[9px] text-muted-3">SELL</span>
                                </span>
                              ) : t.current_ce_ltp ? (
                                <span title={`LTP · basis: ${t.pnl_basis || 'reference'}`}>
                                  ₹{fmt(t.current_ce_ltp)}
                                </span>
                              ) : (
                                <span className="text-muted-3">—</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right font-mono-data text-[10px]" data-testid={`vwap-bounce-open-pnl-${i}`}>
                              {t.pnl_pct != null ? (
                                <span style={{ color: (t.pnl_pct ?? 0) >= 0 ? "var(--bull)" : "var(--bear)" }}>
                                  {(t.pnl_pct ?? 0) >= 0 ? "+" : ""}{fmt(t.pnl_pct, 2)}%
                                </span>
                              ) : <span className="text-muted-3">—</span>}
                            </td>
                            <td className="px-4 py-2 text-left text-[10px] font-mono-data uppercase tracking-wider">
                              {t.live_trade
                                ? <>
                                    <span style={{ color: "var(--bull)" }}>LIVE</span>
                                    {t.live_buy_order_id && (
                                      <div className="text-[9px] normal-case tracking-normal text-muted-3 font-mono-data mt-0.5" title={t.live_buy_order_id}>
                                        #{String(t.live_buy_order_id).slice(-8)}
                                      </div>
                                    )}
                                  </>
                                : <>
                                    <span className="text-muted-3">PAPER</span>
                                    {(t.live_skip_reason || t.live_error) && (
                                      <div
                                        className="text-[9px] normal-case tracking-normal mt-0.5 font-mono-data leading-tight"
                                        style={{ color: t.live_error ? "var(--bear)" : "var(--warn, #d97706)", maxWidth: 280 }}
                                        title={t.live_skip_reason || t.live_error}
                                        data-testid={`vwap-bounce-open-skip-${i}`}
                                      >
                                        {t.live_error ? "Broker: " : "Filter: "}{t.live_skip_reason || t.live_error}
                                      </div>
                                    )}
                                  </>}
                            </td>
                            <td className="px-4 py-2 text-right font-mono-data text-[10px]">
                              <span style={{ color: "var(--bull)" }}>+{fmt(t.target_pct ?? 9, 1)}%</span>
                              <span className="text-muted-3"> / </span>
                              <span style={{ color: "var(--bear)" }}>{fmt(t.stop_loss_pct ?? -15, 1)}%</span>
                            </td>
                            <td className="px-4 py-2 text-right">
                              <button
                                onClick={() => onManualExit(t.id, t.symbol)}
                                data-testid={`vwap-bounce-exit-${i}`}
                                className="text-[10px] uppercase tracking-[0.2em] font-mono-data px-2 py-1 border border-grid hover:bg-white/[0.04]"
                              >
                                Exit
                              </button>
                            </td>
                          </tr>
                          {journeyOpen.has(t.id) && hasExecutionJourney(t) && (
                            <ExecutionJourney trade={t} colSpan={16} />
                          )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  )}
                </div>
              )}

              {/* Closed trades (last 2 days) */}
              {trades?.trades && trades.trades.filter((t) => t.status !== "OPEN").length > 0 && (
                <div className="border border-grid">
                  <div className="px-4 py-2 border-b border-grid text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Check size={12} /> Closed Trades (2d) · {trades.trades.filter((t) => t.status !== "OPEN").length}
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={`${API}/vwap-bounce/export.csv`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="vwap-bounce-export-csv"
                        className="inline-flex items-center gap-1.5 px-2 py-1 border border-grid text-[10px] uppercase tracking-[0.2em] font-mono-data hover:text-white"
                        title="Export all closed trades (full data) as CSV"
                      >
                        <Download size={12} /> Export CSV
                      </a>
                      <CollapseToggle
                        collapsed={closedCollapsed}
                        onToggle={toggleClosed}
                        testid="vwap-bounce-closed-toggle"
                      />
                    </div>
                  </div>
                  {!closedCollapsed && (
                  <div className="overflow-x-auto" data-testid="vwap-bounce-closed-body">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-grid text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
                          <th className="px-4 py-2 text-left">Entered</th>
                          <th className="px-4 py-2 text-left">Closed</th>
                          <th className="px-4 py-2 text-left">Symbol</th>
                          <th className="px-4 py-2 text-left">Dir</th>
                          <th className="px-4 py-2 text-right">Strike</th>
                          <th className="px-4 py-2 text-right" title="Relative Volume at entry (latest 5m / avg-10)">RVOL</th>
                          <th className="px-4 py-2 text-right" title="Underlying's intraday RSI on the 5-min chart at entry (Setup A/C). Setup A uses RSI(9), cross-day lookback (2026-09-21) — real value from market open, not blank until ~10 AM. Setup C highlights <35 as oversold; Setup A already requires 55-75 to enter so it's shown plain, no highlight.">Intraday RSI</th>
                          <th className="px-4 py-2 text-right" title="Setup A only. Parallel, observational RSI(14), cross-day lookback — not a gate, kept for RSI(9) vs RSI(14) comparison.">RSI(14)</th>
                          <th className="px-4 py-2 text-right" title="Setup A only. ADX(14) at entry, cross-day lookback (2026-09-21) so it's a real value from market open, not blank until ~11:45 AM. Must be ≥ 25 to score the adx step's 10 points.">ADX</th>
                          <th className="px-4 py-2 text-center" title="Setup A only. 15-min-timeframe trend check at entry (price above its own VWAP and 20-EMA, last 15m candle bullish) — cross-day lookback (2026-09-21) so it's real from market open, not blank until ~2:45 PM. Worth 10 of the 100 score points.">15m HTF</th>
                          <th className="px-4 py-2 text-right" title="Option premium's own move since today's 9:15 AM open, at entry time">Opt % Chg</th>
                          <th className="px-4 py-2 text-right">Buy LTP</th>
                          <th className="px-4 py-2 text-right">Exit LTP</th>
                          <th className="px-4 py-2 text-right">P&amp;L</th>
                          <th className="px-4 py-2 text-left">Reason</th>
                          <th className="px-4 py-2 text-left">Mode</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trades.trades.filter((t) => t.status !== "OPEN").slice(0, 20).map((t, i) => (
                          <React.Fragment key={t.id}>
                          <tr className="border-b border-grid">
                            <td className="px-4 py-2 text-muted-2 font-mono-data">
                              <div className="inline-flex items-center gap-1">
                                {hasExecutionJourney(t) && (
                                  <button
                                    onClick={() => toggleJourney(t.id)}
                                    aria-expanded={journeyOpen.has(t.id)}
                                    aria-label={journeyOpen.has(t.id) ? "Hide Execution Journey" : "Show Execution Journey"}
                                    data-testid={`vwap-bounce-closed-journey-toggle-${i}`}
                                    className="text-muted-2 hover:text-primary"
                                    title="Toggle Execution Journey"
                                  >
                                    {journeyOpen.has(t.id) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                  </button>
                                )}
                                <span>{toIST(t.entered_at)}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-muted-2 font-mono-data">{toIST(t.closed_at || t.entered_at)}</td>
                            <td className="px-4 py-2 font-semibold">{t.symbol}</td>
                            <td className="px-4 py-2">
                              <DirectionBadge direction={t.direction} setup={t.setup_type} testid={`vwap-bounce-closed-dir-${i}`} />
                            </td>
                            <td className="px-4 py-2 text-right font-mono-data">
                              {t.atm_strike} {((t.direction || "LONG").toUpperCase() === "SHORT") ? "PE" : "CE"}
                            </td>
                            <td
                              className="px-4 py-2 text-right font-mono-data"
                              data-testid={`vwap-bounce-closed-rvol-${i}`}
                              style={{ color: t.rvol_exhaustion ? "var(--warn, #d97706)" : (t.rvol ?? 0) >= 1.5 ? "var(--bull)" : "var(--text-muted)" }}
                              title={t.rvol_exhaustion ? "High RVOL at entry — possible climax/exhaustion volume" : undefined}
                            >
                              {t.rvol != null ? `${fmt(t.rvol, 2)}×${t.rvol_exhaustion ? " ⚠" : ""}` : <span className="text-muted-3">—</span>}
                            </td>
                            <td
                              className="px-4 py-2 text-right font-mono-data"
                              data-testid={`vwap-bounce-closed-rsi-intraday-${i}`}
                              // 2026-09-21 — see the open-trades table's matching
                              // comment: oversold-below-35 highlight stays Setup
                              // C-only, Setup A shows the plain value.
                              style={{ color: t.setup_type === "C" && t.rsi_intraday != null && t.rsi_intraday < 35 ? "var(--bear)" : "var(--text-muted)" }}
                              title={t.setup_type === "C" && t.rsi_intraday != null && t.rsi_intraday < 35 ? "Below 35 — underlying already deeply oversold on the 5-min chart" : ""}
                            >
                              {t.rsi_intraday != null ? fmt(t.rsi_intraday, 1) : <span className="text-muted-3">—</span>}
                            </td>
                            <td
                              className="px-4 py-2 text-right font-mono-data text-muted-2"
                              data-testid={`vwap-bounce-closed-rsi14-${i}`}
                            >
                              {t.rsi_14_intraday != null ? fmt(t.rsi_14_intraday, 1) : <span className="text-muted-3">—</span>}
                            </td>
                            <td
                              className="px-4 py-2 text-right font-mono-data"
                              data-testid={`vwap-bounce-closed-adx-${i}`}
                              style={{ color: t.adx_at_entry != null && t.adx_at_entry >= 25 ? "var(--bull)" : "var(--text-muted)" }}
                            >
                              {t.adx_at_entry != null ? fmt(t.adx_at_entry, 1) : <span className="text-muted-3">—</span>}
                            </td>
                            <td
                              className="px-4 py-2 text-center font-mono-data"
                              data-testid={`vwap-bounce-closed-htf15m-${i}`}
                              style={{ color: t.htf_15m_ok === true ? "var(--bull)" : t.htf_15m_ok === false ? "var(--bear)" : "var(--text-muted)" }}
                            >
                              {t.htf_15m_ok === true ? "Yes" : t.htf_15m_ok === false ? "No" : <span className="text-muted-3">—</span>}
                            </td>
                            <td
                              className="px-4 py-2 text-right font-mono-data"
                              data-testid={`vwap-bounce-closed-optmove-${i}`}
                              title={t.opt_day_open != null ? `Day open ₹${fmt(t.opt_day_open)} · chase limit ${fmt(t.opt_chase_threshold_pct ?? 50, 0)}%` : ""}
                              style={{
                                color: t.opt_move_pct == null ? "var(--text-muted)"
                                  : t.opt_move_pct >= (t.opt_chase_threshold_pct ?? 50) * 0.7 ? "#FFB66E"
                                  : "var(--text-muted)",
                              }}
                            >
                              {t.opt_move_pct == null
                                ? <span className="text-muted-3">—</span>
                                : `${t.opt_move_pct >= 0 ? "+" : ""}${fmt(t.opt_move_pct, 0)}%`}
                            </td>
                            <td className="px-4 py-2 text-right font-mono-data" data-testid={`vwap-bounce-closed-entry-${i}`}>
                              {t.live_trade && t.live_avg_fill_price ? `₹${fmt(t.live_avg_fill_price)}` : (t.atm_ce_ltp != null ? `₹${fmt(t.atm_ce_ltp)}` : <span className="text-muted-3">—</span>)}
                            </td>
                            <td className="px-4 py-2 text-right font-mono-data" data-testid={`vwap-bounce-closed-exit-${i}`}>
                              {t.live_sell_price ? `₹${fmt(t.live_sell_price)}` : (t.close_ce_ltp != null ? `₹${fmt(t.close_ce_ltp)}` : <span className="text-muted-3">—</span>)}
                            </td>
                            <td className="px-4 py-2 text-right font-mono-data text-[10px]" data-testid={`vwap-bounce-closed-pnl-${i}`}>
                              {t.final_pnl_pct != null ? (
                                <span style={{ color: (t.final_pnl_pct ?? 0) >= 0 ? "var(--bull)" : "var(--bear)" }}>
                                  {(t.final_pnl_pct ?? 0) >= 0 ? "+" : ""}{fmt(t.final_pnl_pct, 2)}%
                                </span>
                              ) : <span className="text-muted-3">—</span>}
                            </td>
                            <td className="px-4 py-2 text-left text-muted-2 font-mono-data">{t.close_reason || "—"}</td>
                            <td className="px-4 py-2 text-left text-[10px] font-mono-data uppercase tracking-wider">
                              {t.live_trade
                                ? <span style={{ color: "var(--bull)" }}>LIVE</span>
                                : <>
                                    <span className="text-muted-3">PAPER</span>
                                    {(t.live_skip_reason || t.live_error) && (
                                      <div
                                        className="text-[9px] normal-case tracking-normal mt-0.5 font-mono-data leading-tight"
                                        style={{ color: t.live_error ? "var(--bear)" : "var(--warn, #d97706)", maxWidth: 240 }}
                                        title={t.live_skip_reason || t.live_error}
                                      >
                                        {t.live_error ? "Broker: " : "Filter: "}{t.live_skip_reason || t.live_error}
                                      </div>
                                    )}
                                  </>}
                            </td>
                          </tr>
                          {journeyOpen.has(t.id) && hasExecutionJourney(t) && (
                            <ExecutionJourney trade={t} colSpan={16} />
                          )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
};
